// SPDX-License-Identifier: BUSL-1.1

/**
 * Console → Archive deep-link builder (cross-surface affordance; ADR 009).
 *
 * The Archive is a separate SSR surface served from its own origin. This
 * helper builds the cross-origin URLs the Console links out to, so a moderator
 * inspecting a run in the Console can jump straight to that run's read-only
 * timeline + evidence view instead of hand-typing the URL (the #1 adoption
 * friction in the Archive adoption review, F1).
 *
 * Origin is configurable per deployment via `VITE_ARCHIVE_URL` (mirrors the
 * `VITE_*` external-link pattern in `external-links.ts`; domain binding is a
 * separate infra task). Default is the production origin.
 *
 * Id mapping: the Archive routes are `/e/:agentId/:episodeId` and
 * `/audit/:agentId/:episodeId`. The G1 endpoint
 * (`GET /api/agents/:agentId/episodes/:episodeId`) applies tenant/org authz on
 * the agent FIRST, then resolves the run scoped to both ids — so the URL MUST
 * carry the agent. The Console passes the run's `agentId` plus the run's own id
 * (a job id or a session id) verbatim as the episode id. No transformation:
 * episodeId === runId (ADR 010 §Decision-log 2026-05-20, NocoDB #4843).
 *
 * Vocabulary discipline (ADR 009 §4 / bridge-001): this module deals in URLs
 * only. Archive brand vocabulary (EPISODE / CHANNEL / EVIDENCE CHAIN / WATCH)
 * is intentionally kept OUT of the path-segment constants' surrounding labels —
 * the consumer component owns the (neutral) link text.
 */

/** Default Archive origin; overridden per-deploy by `VITE_ARCHIVE_URL`. */
const DEFAULT_ARCHIVE_ORIGIN = 'https://archive.build.ceo';

/**
 * The configured Archive origin, with no trailing slash. Reads
 * `VITE_ARCHIVE_URL` at build time and falls back to the production origin so
 * the link works out of the box; deployments point it at their own Archive.
 */
export function archiveOrigin(): string {
  const configured = import.meta.env.VITE_ARCHIVE_URL as string | undefined;
  const origin = configured && configured.length > 0 ? configured : DEFAULT_ARCHIVE_ORIGIN;
  return origin.replace(/\/+$/, '');
}

/**
 * URL for a run's Episode (timeline + evidence). `agentId` is the run's owning
 * agent (G1 authorizes it before resolving the run); `runId` is the Console
 * run's own id (job id or session id) — passed verbatim as the Archive episode
 * id.
 */
export function episodeUrl(agentId: string, runId: string): string {
  return `${archiveOrigin()}/e/${encodeURIComponent(agentId)}/${encodeURIComponent(runId)}`;
}

/**
 * URL for a run's single-run Auditor view (role's-view + posture rail). Same
 * `(agentId, runId)` → `/audit/:agentId/:episodeId` mapping as {@link episodeUrl}.
 */
export function auditorUrl(agentId: string, runId: string): string {
  return `${archiveOrigin()}/audit/${encodeURIComponent(agentId)}/${encodeURIComponent(runId)}`;
}
